import { useReducer, useRef, useCallback } from 'react';
import { createPointClickHandler } from '../handlers/handlePointClick';

/**
 * Pure function that computes percentage change between two values.
 * Returns 0 if either value is falsy (null, undefined, 0) or if the result is NaN/Infinity.
 *
 * @param {number|null} before - The starting value
 * @param {number|null} after - The ending value
 * @returns {number} The percentage change: ((after - before) / before) * 100
 */
export function calculateChange(before, after) {
  if (before == null || after == null || before === 0) {
    return 0;
  }

  const calculated = ((after - before) / before) * 100;

  if (!isFinite(calculated)) {
    return 0;
  }

  return calculated;
}

const initialState = { before: null, after: null, changeAnn: 0, pointCount: 0 };

function reducer(state, action) {
  switch (action.type) {
    case 'FIRST_CLICK':
      return { before: action.y, after: null, changeAnn: 0, pointCount: 1 };
    case 'SECOND_CLICK':
      return { ...state, after: action.y, changeAnn: action.changeAnn, pointCount: 2 };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

/**
 * Custom hook that encapsulates point click tracking, change calculation,
 * and annualized return computation for the FunViz chart.
 */
const useChartInteraction = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const pointCountRef = useRef(0);

  // Sync ref for synchronous access in the click handler
  pointCountRef.current = state.pointCount;

  const handlePointClick = useCallback(
    () => createPointClickHandler(pointCountRef, dispatch),
    []
  )();

  // Derived — no state, no effect
  const change = calculateChange(state.before, state.after);

  const resetPoints = useCallback(() => dispatch({ type: 'RESET' }), []);

  return {
    before: state.before,
    after: state.after,
    change,
    changeAnn: state.changeAnn,
    pointCount: state.pointCount,
    handlePointClick,
    resetPoints,
  };
};

export default useChartInteraction;
